import { getCookieDomainAttribute } from "./publicApp";

const AUTH_TOKEN_KEY = "authToken";
const AUTH_TOKEN_COOKIE_KEY = "buzzvibe_auth_token";
const AUTH_USER_CACHE_KEY = "buzzvibe.auth.user";
const AUTH_SHOPS_CACHE_KEY = "buzzvibe.auth.shops";
const AUTH_CACHE_TTL_MS = 15 * 60 * 1000;
const AUTH_TOKEN_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

const getStorageItem = (storage, key) => {
  try {
    return storage?.getItem(key) || null;
  } catch (error) {
    return null;
  }
};

const setStorageItem = (storage, key, value) => {
  try {
    if (!storage) return;

    if (value === null || value === undefined) {
      storage.removeItem(key);
      return;
    }

    storage.setItem(key, value);
  } catch (error) {
    // Storage can be blocked in private modes; cookies still carry the real auth session.
  }
};

const removeStorageItem = (storage, key) => {
  try {
    storage?.removeItem(key);
  } catch (error) {
    // Ignore storage cleanup failures.
  }
};

const getBrowserStorage = () => {
  if (typeof window === "undefined") {
    return { sessionStorage: null, localStorage: null };
  }

  return {
    sessionStorage: window.sessionStorage,
    localStorage: window.localStorage,
  };
};

const getCookieBaseAttributes = () => {
  if (typeof window === "undefined") {
    return "Path=/; SameSite=Lax";
  }

  const secureAttribute = window.location.protocol === "https:" ? "; Secure" : "";
  return `Path=/; SameSite=Lax${secureAttribute}`;
};

const readCookie = (name) => {
  if (typeof document === "undefined") {
    return null;
  }

  const cookiePrefix = `${name}=`;
  const cookies = document.cookie ? document.cookie.split("; ") : [];

  for (const cookie of cookies) {
    if (cookie.startsWith(cookiePrefix)) {
      return decodeURIComponent(cookie.slice(cookiePrefix.length));
    }
  }

  return null;
};

const writeAuthTokenCookie = (token) => {
  if (typeof document === "undefined") {
    return;
  }

  const baseAttributes = getCookieBaseAttributes();
  const domainAttribute = getCookieDomainAttribute();

  if (token) {
    document.cookie = `${AUTH_TOKEN_COOKIE_KEY}=${encodeURIComponent(token)}; Max-Age=${AUTH_TOKEN_COOKIE_MAX_AGE_SECONDS}; ${baseAttributes}${domainAttribute}`;
    return;
  }

  document.cookie = `${AUTH_TOKEN_COOKIE_KEY}=; Max-Age=0; ${baseAttributes}`;

  if (domainAttribute) {
    document.cookie = `${AUTH_TOKEN_COOKIE_KEY}=; Max-Age=0; ${baseAttributes}${domainAttribute}`;
  }
};

const writeCache = (key, value, ttlMs = AUTH_CACHE_TTL_MS) => {
  const { sessionStorage, localStorage } = getBrowserStorage();
  const payload = JSON.stringify({
    value,
    expiresAt: Date.now() + ttlMs,
  });

  setStorageItem(sessionStorage, key, payload);
  setStorageItem(localStorage, key, payload);
};

const readCache = (key) => {
  const { sessionStorage, localStorage } = getBrowserStorage();
  const rawValue = getStorageItem(sessionStorage, key) || getStorageItem(localStorage, key);

  if (!rawValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue);

    if (!parsedValue || parsedValue.expiresAt < Date.now()) {
      removeStorageItem(sessionStorage, key);
      removeStorageItem(localStorage, key);
      return null;
    }

    return parsedValue.value || null;
  } catch (error) {
    removeStorageItem(sessionStorage, key);
    removeStorageItem(localStorage, key);
    return null;
  }
};

export const getStoredAuthToken = () => {
  const { sessionStorage, localStorage } = getBrowserStorage();
  return getStorageItem(sessionStorage, AUTH_TOKEN_KEY) || getStorageItem(localStorage, AUTH_TOKEN_KEY) || readCookie(AUTH_TOKEN_COOKIE_KEY);
};

export const persistAuthToken = (token) => {
  const normalizedToken = typeof token === "string" ? token.trim() : "";
  const { sessionStorage, localStorage } = getBrowserStorage();

  if (!normalizedToken || normalizedToken === "undefined" || normalizedToken === "null") {
    removeStorageItem(sessionStorage, AUTH_TOKEN_KEY);
    removeStorageItem(localStorage, AUTH_TOKEN_KEY);
    writeAuthTokenCookie(null);
    return null;
  }

  setStorageItem(sessionStorage, AUTH_TOKEN_KEY, normalizedToken);
  setStorageItem(localStorage, AUTH_TOKEN_KEY, normalizedToken);
  writeAuthTokenCookie(normalizedToken);
  return normalizedToken;
};

export const cacheAuthUser = (user) => {
  if (!user || typeof user !== "object") {
    return;
  }

  writeCache(AUTH_USER_CACHE_KEY, user);
};

export const getCachedAuthUser = () => {
  const user = readCache(AUTH_USER_CACHE_KEY);
  return user && typeof user === "object" ? user : null;
};

export const cacheUserShops = (shops) => {
  if (!Array.isArray(shops)) {
    return;
  }

  writeCache(AUTH_SHOPS_CACHE_KEY, shops);
};

export const getCachedUserShops = () => {
  const shops = readCache(AUTH_SHOPS_CACHE_KEY);
  return Array.isArray(shops) ? shops : [];
};

export const clearAuthSession = () => {
  const { sessionStorage, localStorage } = getBrowserStorage();

  [AUTH_TOKEN_KEY, AUTH_USER_CACHE_KEY, AUTH_SHOPS_CACHE_KEY].forEach((key) => {
    removeStorageItem(sessionStorage, key);
    removeStorageItem(localStorage, key);
  });
  writeAuthTokenCookie(null);
};
